import React, {
  useEffect, useCallback, useMemo, useState, Fragment, ReactElement,
} from 'react';
import Card from '../../../components/card/card.component';
import Table from '../../../components/table/table.component';
import TableBody from '../../../components/table-body/table-body.component';
import TableRow from '../../../components/table-row/table-row.component';
import TableCell from '../../../components/table-cell/table-cell.component';
import Button from '../../../components/button/button.component';
import ModalButtonBlock from '../../../components/modal-button-block/modal-button-block.component';
import LeaveConfirmModal from '../../../components/leave-confirm-modal/leave-confirm-modal.component';
import RequiredMark from '../../../components/required-mark/required-mark.component';
import TextBlock from '../../../components/text-block/text-block.component';
import ModalLayout from '../../../components/modal-layout/modal-layout.component';
import Loader from '../../../components/loader/loader.component';
import Form from '../../../components/form/form.component';
import FormFieldSelect from '../../../components/form-field-select/form-field-select.component';
import FormFieldText from '../../../components/form-field-text/form-field-text.component';
import FormFieldNumber from '../../../components/form-field-number/form-field-number.component';
import MenuItemList from '../../../components/menu-item-list/menu-item-list.component';
import ControlButtonBlock from '../../app/common/control-button-block/control-button-block.component';
import FormService from '../../../services/form-service';
import {
  DEAL_CAPITALIZATION_CATEGORY_SOURCES, DEAL_CAPITALIZATION_CATEGORY_USES,
} from '../../../constants/entity/deal-capitalization-categories';
import {
  DEAL_CAPITALIZATION_GROUP_COSTS, DEAL_CAPITALIZATION_GROUP_DEBT, DEAL_CAPITALIZATION_GROUP_EQUITY,
} from '../../../constants/entity/deal-capitalization-groups';

const DealCapitalizationModal: ReactMemoComponent = React.memo((props: ReactComponentProps): ReactElement => {
  const MODAL_TITLE_PREFIX_DEFAULT: string = 'New';
  const MODAL_TITLE_PREFIX_EDIT: string = 'Edit';

  const {
    isOpen,
    isSaving,
    isSaveDisabled,
    isEditMode,
    isNewDeal,
    isModalEditMode,
    isRightDrawerOpen,
    isSimpleView,
    isNewEntity,
    dealCapitalizationItemData,
    dealCapitalizationGroupData,
    dealCapitalizationCategory,
    groupTitle,
    editPermission,
    dealCapitalizationTypeList,
    dealCapitalizationGroupList,
    onCancelForm,
    onSubmitForm,
    onCloseModal,
    onCancel,
    onSwitchModalEditMode,
  } = props;

  const [isLeaveConfirmModalOpen, setIsLeaveConfirmModalOpen]: [boolean, Function] = useState(false);
  const [selectedDealCapitalizationGroup, setSelectedDealCapitalizationGroup]: [string | null, Function]
    = useState(null);

  const DEAL_CAPITALIZATION_GROUP_MAP: Record<string, string[]> = useMemo((): Record<string, string[]> => ({
    [DEAL_CAPITALIZATION_CATEGORY_SOURCES]: [DEAL_CAPITALIZATION_GROUP_DEBT, DEAL_CAPITALIZATION_GROUP_EQUITY],
    [DEAL_CAPITALIZATION_CATEGORY_USES]: [DEAL_CAPITALIZATION_GROUP_COSTS],
  }), []);

  const dealCapitalizationGroup: DealCapitalizationGroup = useMemo((): DealCapitalizationGroup => (
    dealCapitalizationGroupData && dealCapitalizationGroupData.name && dealCapitalizationGroupList
    && dealCapitalizationGroupList.find((dealCapitalizationGroupItem: DealCapitalizationGroup): boolean => (
      dealCapitalizationGroupItem.title === dealCapitalizationGroupData.name.trim()
    ))
  ), [dealCapitalizationGroupData, dealCapitalizationGroupList]);
  const dealCapitalizationGroupName: string | null = dealCapitalizationGroup
    ? dealCapitalizationGroup.name : selectedDealCapitalizationGroup;
  const filteredDealCapitalizationTypeList: DealCapitalizationType[] = useMemo((): DealCapitalizationType[] => (
    dealCapitalizationGroupName && dealCapitalizationTypeList
    && dealCapitalizationTypeList.filter((dealCapitalizationTypeItem: DealCapitalizationType): boolean => (
      dealCapitalizationTypeItem.group === dealCapitalizationGroupName
    ))
  ), [dealCapitalizationGroupName, dealCapitalizationTypeList]);
  const filteredDealCapitalizationGroupList: DealCapitalizationGroup[] = useMemo((): DealCapitalizationGroup[] => (
    dealCapitalizationGroupList && dealCapitalizationGroupList.filter((
      dealCapitalizationGroupItem: DealCapitalizationGroup,
    ): boolean => (
      dealCapitalizationCategory && DEAL_CAPITALIZATION_GROUP_MAP[dealCapitalizationCategory]
      && DEAL_CAPITALIZATION_GROUP_MAP[dealCapitalizationCategory].includes(dealCapitalizationGroupItem.name)
    ))
  ), [dealCapitalizationGroupList, dealCapitalizationCategory]);

  const modalTitle: string = useMemo((): string => {
    if (dealCapitalizationItemData && dealCapitalizationItemData.name) {
      return `${(isEditMode || isModalEditMode)
        ? `${MODAL_TITLE_PREFIX_EDIT} ` : ''}${dealCapitalizationItemData.name}`;
    }

    return `${MODAL_TITLE_PREFIX_DEFAULT} ${groupTitle}`;
  }, [dealCapitalizationItemData, groupTitle, isEditMode, isModalEditMode]);

  const dealCapitalizationItemId: number = (!isNewEntity && dealCapitalizationItemData && dealCapitalizationItemData.id)
    || FormService.FORM_KEY_NEW;
  const dealCapitalizationGroupId: number = (!isNewEntity && dealCapitalizationGroupData
    && dealCapitalizationGroupData.id) || FormService.FORM_KEY_NEW;
  const formKeyDealCapitalizationItem: string = FormService.getIndexedFormFieldKey(dealCapitalizationItemId);
  const formKeyDealCapitalizationGroup: string = FormService.getIndexedFormFieldKey(dealCapitalizationGroupId);
  const formPrefix: string = `dealData.dealCapitalizationData.${dealCapitalizationCategory}`
    + `[${formKeyDealCapitalizationGroup}].items[${formKeyDealCapitalizationItem}]`;
  const withEditModeSwitcher: boolean = !isNewEntity && !isNewDeal && !isEditMode;

  const menuItemDealCapitalizationGroupList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: filteredDealCapitalizationGroupList,
  }), [filteredDealCapitalizationGroupList]);

  const menuItemDealCapitalizationTypeList: ReactElement[] = useMemo((): ReactElement[] => MenuItemList({
    optionItemValueKey: 'name',
    optionList: filteredDealCapitalizationTypeList,
  }), [filteredDealCapitalizationTypeList]);

  const resetForm: Function = useCallback((): void => {
    setSelectedDealCapitalizationGroup(dealCapitalizationCategory === DEAL_CAPITALIZATION_CATEGORY_SOURCES
      ? DEAL_CAPITALIZATION_GROUP_DEBT : DEAL_CAPITALIZATION_GROUP_COSTS);
  }, [dealCapitalizationCategory]);

  const changeDealCapitalizationGroup: Function = useCallback((dealCapitalizationGroupName: string): void => {
    setSelectedDealCapitalizationGroup(dealCapitalizationGroupName);
  }, []);

  const closeModalByClickAway: Function = useCallback((): void => {
    if (!isSaveDisabled && isModalEditMode) {
      setIsLeaveConfirmModalOpen(true);
    } else {
      if (isModalEditMode && onCancel) {
        onCancel();
      }

      if (onCloseModal) {
        onCloseModal();
      }

      resetForm();
    }
  }, [isSaveDisabled, isModalEditMode, dealCapitalizationCategory]);

  const closeLeaveConfirmModal: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);
  }, []);

  const onLeaveConfirm: Function = useCallback((): void => {
    setIsLeaveConfirmModalOpen(false);

    resetForm();

    if (onCancel) {
      onCancel();
    }

    if (onCloseModal) {
      onCloseModal();
    }
  }, [dealCapitalizationCategory]);

  const closeModal: MouseEventHandlerFunction = useCallback((): void => {
    resetForm();

    if (onCloseModal) {
      onCloseModal();
    }
  }, [dealCapitalizationCategory]);

  const cancelForm: Function = useCallback((): void => {
    if (onCloseModal) {
      onCloseModal();
    }

    if (onCancelForm) {
      onCancelForm();
    }
  }, [dealCapitalizationCategory]);

  useEffect((): void => {
    if (!selectedDealCapitalizationGroup || dealCapitalizationCategory !== selectedDealCapitalizationGroup) {
      resetForm();
    }
  }, [dealCapitalizationCategory]);

  return (
    <Fragment>
      {
        !!dealCapitalizationItemData && (
          <ModalLayout
            isOpen={isOpen}
            isModalEditMode={isModalEditMode}
            withEditModeSwitcher={withEditModeSwitcher}
            title={modalTitle}
            editPermission={editPermission}
            onCloseModal={closeModal}
            onCloseModalByClickAway={closeModalByClickAway}
            onSwitchModalEditMode={onSwitchModalEditMode}
          >
            <Form>
              <Card isContainer withNoPadding>
                <Table>
                  <TableBody isPlain withNoBorders withTwoColumns withHeaderColumn>
                    <TableRow>
                      <TableCell>
                        {`${groupTitle} Name`}
                        {
                          (isEditMode || isModalEditMode || isNewDeal || isNewEntity) && (
                            <RequiredMark />
                          )
                        }
                      </TableCell>
                      <TableCell>
                        {
                          (isEditMode || isModalEditMode || isNewDeal || isNewEntity) ? (
                            <FormFieldText isBordered name={`${formPrefix}.name`} />
                          ) : (dealCapitalizationItemData.name || '')
                        }
                      </TableCell>
                    </TableRow>
                    {
                      isNewEntity && (
                        <TableRow>
                          <TableCell>
                            <TextBlock>
                              Group
                            </TextBlock>
                            <RequiredMark />
                          </TableCell>
                          <TableCell>
                            <FormFieldSelect
                              isBordered
                              isDisabled={filteredDealCapitalizationGroupList
                                && !filteredDealCapitalizationGroupList.length}
                              name={`${formPrefix}.group`}
                              optionsTotal={filteredDealCapitalizationGroupList
                                && filteredDealCapitalizationGroupList.length}
                              onChange={((dealCapitalizationGroupName: string): void => {
                                changeDealCapitalizationGroup(dealCapitalizationGroupName);
                              })}
                            >
                              {menuItemDealCapitalizationGroupList}
                            </FormFieldSelect>
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      ((isEditMode || isModalEditMode || isNewDeal || isNewEntity)
                        || (!isSimpleView && dealCapitalizationItemData.type && dealCapitalizationItemData.type.title))
                      && (
                        <TableRow>
                          <TableCell>
                            <TextBlock>
                              Type
                            </TextBlock>
                            {
                              (isEditMode || isModalEditMode || isNewDeal || isNewEntity) && (
                                <RequiredMark />
                              )
                            }
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isModalEditMode || isNewDeal || isNewEntity) ? (
                                <FormFieldSelect
                                  isBordered
                                  withBlankElement
                                  isDisabled={(filteredDealCapitalizationTypeList
                                    && !filteredDealCapitalizationTypeList.length) || !dealCapitalizationGroupName}
                                  name={`${formPrefix}.type`}
                                  optionsTotal={filteredDealCapitalizationTypeList
                                    && filteredDealCapitalizationTypeList.length}
                                >
                                  {menuItemDealCapitalizationTypeList}
                                </FormFieldSelect>
                              ) : ((dealCapitalizationItemData.type && dealCapitalizationItemData.type.title) || '')
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      ((isEditMode || isModalEditMode || isNewDeal || isNewEntity)
                        || (!isSimpleView && dealCapitalizationItemData.atClosingAmount)) && (
                        <TableRow>
                          <TableCell>
                            At Closing, $
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isModalEditMode || isNewDeal || isNewEntity) ? (
                                <FormFieldNumber isBordered name={`${formPrefix}.atClosingAmount`} />
                              ) : (dealCapitalizationItemData.atClosingAmount || '')
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      ((isEditMode || isModalEditMode || isNewDeal || isNewEntity)
                        || (!isSimpleView && dealCapitalizationItemData.futureAmount)) && (
                        <TableRow>
                          <TableCell>
                            Future, $
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isModalEditMode || isNewDeal || isNewEntity) ? (
                                <FormFieldNumber isBordered name={`${formPrefix}.futureAmount`} />
                              ) : (dealCapitalizationItemData.futureAmount || '')
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      ((isEditMode || isModalEditMode || isNewDeal || isNewEntity)
                        || (!isSimpleView && dealCapitalizationItemData.amount)) && (
                        <TableRow>
                          <TableCell>
                            Total Amount, $
                          </TableCell>
                          <TableCell>
                            {
                              (isEditMode || isModalEditMode || isNewDeal || isNewEntity) ? (
                                <FormFieldNumber isBordered name={`${formPrefix}.amount`} />
                              ) : (dealCapitalizationItemData.amount || '')
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      !isEditMode && !isModalEditMode && !isNewDeal && !isSimpleView && !isNewEntity
                      && dealCapitalizationItemData.nsfAmount && (
                        <TableRow>
                          <TableCell>
                            $ / NSF
                          </TableCell>
                          <TableCell>
                            {dealCapitalizationItemData.nsfAmount || ''}
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      !isEditMode && !isModalEditMode && !isNewDeal && !isSimpleView && !isNewEntity
                      && dealCapitalizationItemData.gsfAmount && (
                        <TableRow>
                          <TableCell>
                            $ / GSF
                          </TableCell>
                          <TableCell>
                            {dealCapitalizationItemData.gsfAmount || ''}
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      !isEditMode && !isModalEditMode && !isNewDeal && !isSimpleView && !isNewEntity
                      && dealCapitalizationItemData.zfaAmount && (
                        <TableRow>
                          <TableCell>
                            $ / ZFA
                          </TableCell>
                          <TableCell>
                            {dealCapitalizationItemData.zfaAmount || ''}
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      !isEditMode && !isModalEditMode && !isNewDeal && !isSimpleView && !isNewEntity
                      && dealCapitalizationItemData.unitsAmount && (
                        <TableRow>
                          <TableCell>
                            $ / Unit
                          </TableCell>
                          <TableCell>
                            {dealCapitalizationItemData.unitsAmount || ''}
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      !isEditMode && !isModalEditMode && !isNewDeal && !isSimpleView && !isNewEntity
                      && dealCapitalizationItemData.keysAmount && (
                        <TableRow>
                          <TableCell>
                            $ / Key
                          </TableCell>
                          <TableCell>
                            {dealCapitalizationItemData.keysAmount || ''}
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      !isEditMode && !isModalEditMode && !isNewDeal && !isSimpleView && !isNewEntity
                      && dealCapitalizationItemData.acresAmount && (
                        <TableRow>
                          <TableCell>
                            $ / Acre
                          </TableCell>
                          <TableCell>
                            {dealCapitalizationItemData.acresAmount || ''}
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      !isEditMode && !isModalEditMode && !isNewDeal && !isSimpleView && !isNewEntity
                      && dealCapitalizationItemData.lotsfAmount && (
                        <TableRow>
                          <TableCell>
                            $ / Lot SF
                          </TableCell>
                          <TableCell>
                            {dealCapitalizationItemData.lotsfAmount || ''}
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      !isEditMode && !isModalEditMode && !isNewDeal && !isSimpleView && !isNewEntity
                      && dealCapitalizationItemData.sharePercent && (
                        <TableRow>
                          <TableCell>
                            % Share
                          </TableCell>
                          <TableCell>
                            {dealCapitalizationItemData.sharePercent || ''}
                          </TableCell>
                        </TableRow>
                      )
                    }
                    {
                      !isSimpleView && dealCapitalizationItemData.notes && (
                        <TableRow>
                          <TableCell>
                            Notes
                          </TableCell>
                          <TableCell isTextMultiline={!isEditMode && !isModalEditMode && !isNewDeal}>
                            {
                              (isEditMode || isModalEditMode || isNewDeal || isNewEntity) ? (
                                <FormFieldText
                                  isBordered
                                  isMultiline
                                  name={`${formPrefix}.notes`}
                                  maxRows={FormService.TEXTAREA_MAX_ROWS_MEDIUM}
                                  minRows={FormService.TEXTAREA_MIN_ROWS_DEFAULT}
                                />
                              ) : (dealCapitalizationItemData.notes || '')
                            }
                          </TableCell>
                        </TableRow>
                      )
                    }
                  </TableBody>
                </Table>
              </Card>
              {
                isModalEditMode || isNewEntity ? (
                  <div>
                    {
                      isSaving && (
                        <Loader isInline isFloating isModal isSubmit />
                      )
                    }
                    <ControlButtonBlock
                      isModal
                      isNewEntity={isNewEntity}
                      isModalEditMode={isModalEditMode}
                      isSaveDisabled={isSaveDisabled}
                      withOffset={isRightDrawerOpen}
                      onCancelForm={cancelForm}
                      onSubmitForm={onSubmitForm}
                    />
                  </div>
                ) : (
                  <ModalButtonBlock>
                    <Button onClick={closeModal}>
                      Close
                    </Button>
                  </ModalButtonBlock>
                )
              }
            </Form>
            <LeaveConfirmModal
              isSubModal
              isOpen={isLeaveConfirmModalOpen}
              onConfirm={onLeaveConfirm}
              onCloseModal={closeLeaveConfirmModal}
            />
          </ModalLayout>
        )
      }
    </Fragment>
  );
});

export default DealCapitalizationModal;
